import { reactive } from 'vue';

/**
 * Global Layout State - Dynamic key-value store with backward compatibility
 */
type LayoutValue = string | number | boolean;

// Internal Map storage for all layout state
const layoutStore = reactive<Map<string, LayoutValue>>(new Map());

// Create reactive proxy object for backward compatibility (layout.navigation.app.state)
const createLayoutProxy = (): any => {
    const handler: ProxyHandler<any> = {
        get(target, prop: string) {
            // If accessing a known property, return nested proxy
            if (typeof prop === 'string' && prop !== 'toJSON') {
                return new Proxy(
                    {},
                    {
                        get(_, nestedProp: string) {
                            // Build full path
                            const path = `${prop}.${String(nestedProp)}`;

                            // Check if this is a leaf value in Map
                            if (layoutStore.has(path)) {
                                return layoutStore.get(path);
                            }

                            // Otherwise return another proxy for deeper nesting
                            return new Proxy(
                                {},
                                {
                                    get(__, deepProp: string) {
                                        const deepPath = `${path}.${String(deepProp)}`;
                                        return layoutStore.get(deepPath);
                                    },
                                    set(__, deepProp: string, value: LayoutValue) {
                                        const deepPath = `${path}.${String(deepProp)}`;
                                        layoutStore.set(deepPath, value);
                                        return true;
                                    },
                                },
                            );
                        },
                        set(_, nestedProp: string, value: LayoutValue) {
                            const path = `${prop}.${String(nestedProp)}`;
                            layoutStore.set(path, value);
                            return true;
                        },
                    },
                );
            }
            return undefined;
        },
    };

    return reactive(new Proxy({}, handler));
};

// Singleton proxy for backward compatibility
const layoutProxy = createLayoutProxy();

export interface UseLayoutReturn {
    /**
     * Set a layout state value by key path
     * @param key - Dot-notation path (e.g., 'navigation.pages.state')
     * @param value - Value to set
     */
    change: (key: string, value: LayoutValue) => void;

    /**
     * Get a layout state value by key path
     * @param key - Dot-notation path
     * @param defaultValue - Default value if key doesn't exist
     */
    get: (key: string, defaultValue?: LayoutValue) => LayoutValue | undefined;

    /**
     * Check if a key exists
     */
    has: (key: string) => boolean;

    /**
     * Remove a key from the store
     */
    remove: (key: string) => boolean;

    /**
     * Clear all layout state
     */
    clear: () => void;

    /**
     * Get all keys
     */
    keys: () => string[];

    /**
     * Reactive layout object for backward compatibility
     * Access via: layout.navigation.app.state
     */
    layout: any;
}

/**
 * Global layout state management composable
 *
 * Supports two access patterns:
 *
 * 1. Modern Map-based (recommended):
 * ```ts
 * const { change, get } = useLayout();
 * change('navigation.app.state', 'expanded');
 * const state = get('navigation.app.state');
 * ```
 *
 * 2. Legacy object-based (backward compatible):
 * ```ts
 * const { change, layout } = useLayout();
 * change('navigation.app.state', 'expanded');
 * const state = layout.navigation.app.state; // reactive
 * ```
 */
export function useLayout(): UseLayoutReturn {
    const change = (key: string, value: LayoutValue) => {
        layoutStore.set(key, value);
    };

    const get = (key: string, defaultValue?: LayoutValue): LayoutValue | undefined => {
        return layoutStore.has(key) ? layoutStore.get(key) : defaultValue;
    };

    const has = (key: string): boolean => {
        return layoutStore.has(key);
    };

    const remove = (key: string): boolean => {
        return layoutStore.delete(key);
    };

    const clear = () => {
        layoutStore.clear();
    };

    const keys = (): string[] => {
        return Array.from(layoutStore.keys());
    };

    return {
        change,
        get,
        has,
        remove,
        clear,
        keys,
        layout: layoutProxy,
    };
}
